/**
 * Carding LMW API client — settings master, alerts, formula calc.
 *
 * Backend: app/api/routers/carding_lmw.py — mounted at /carding/*.
 */
import apiClient from '@/shared/api/client'

// ─── Enums ──────────────────────────────────────────────────────────

export const CARD_ALERT_TYPES = [
  'AUTO_LEVELLER_OFF',
  'HANK_OUTSIDE_TOLERANCE',
  'AUTO_LEVELLER_DEVIATION',
  'EFFICIENCY_BELOW_STANDARD',
  'IDLE_TIME_HIGH',
  'POWER_FAIL',
  'SLIVER_BREAK_HIGH',
  'CHUTE_FAULT_HIGH',
  'DOUBLE_LAP_HIGH',
  'WASTE_ABOVE_STANDARD',
  'CAN_CONTENT_MISMATCH',
  'DELIVERY_SPEED_DEVIATION',
] as const
export type CardAlertType = (typeof CARD_ALERT_TYPES)[number]

export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number]

export const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'DISMISSED'] as const
export type AlertStatus = (typeof ALERT_STATUSES)[number]

export const SEVERITY_COLOR: Record<AlertSeverity, string> = {
  INFO: 'blue',
  WARNING: 'gold',
  CRITICAL: 'red',
}
export const STATUS_COLOR: Record<AlertStatus, string> = {
  OPEN: 'red',
  ACKNOWLEDGED: 'orange',
  RESOLVED: 'green',
  DISMISSED: 'default',
}

// Human-readable labels for the 12 alert types
export const ALERT_TYPE_LABEL: Record<CardAlertType, string> = {
  AUTO_LEVELLER_OFF: 'Auto-leveller OFF',
  HANK_OUTSIDE_TOLERANCE: 'Hank outside tolerance',
  AUTO_LEVELLER_DEVIATION: 'Auto-leveller deviation',
  EFFICIENCY_BELOW_STANDARD: 'Efficiency below standard',
  IDLE_TIME_HIGH: 'Idle time high',
  POWER_FAIL: 'Power failure',
  SLIVER_BREAK_HIGH: 'Sliver breaks high',
  CHUTE_FAULT_HIGH: 'Chute faults high',
  DOUBLE_LAP_HIGH: 'Double laps high',
  WASTE_ABOVE_STANDARD: 'Waste above standard',
  CAN_CONTENT_MISMATCH: 'Can content mismatch',
  DELIVERY_SPEED_DEVIATION: 'Delivery speed deviation',
}

// ─── Types ──────────────────────────────────────────────────────────

export interface CardStandard {
  id: string
  plant_id: string
  machine_id: string | null
  count_id: string | null
  yarn_type: string | null
  sales_order_id: string | null
  card_sliver_hank: number
  delivery_speed_m_min: number
  standard_efficiency_pct: number
  standard_can_kg: number
  standard_waste_pct: number
  // Sprint 0140 — QC metadata
  qc_measured_at: string | null
  qc_measured_by: string | null
  qc_sample_kg: number | null
  // LMW display
  cylinder_speed_rpm: number | null
  flats_speed_mm_min: number | null
  lickerin_1_speed_rpm: number | null
  lickerin_2_speed_rpm: number | null
  lickerin_3_speed_rpm: number | null
  feed_roller_rpm: number | null
  card_draft: number | null
  chute_feed_inverter_hz: number | null
  feed_thickness_mm: number | null
  chute_pressure_pa: number | null
  can_length_m: number | null
  // Alert tolerances
  hank_tolerance_pct: number | null
  correction_allowed_pct: number | null
  idle_minutes_threshold: number | null
  sliver_break_threshold: number | null
  chute_fault_threshold: number | null
  double_lap_threshold: number | null
  effective_from: string
  effective_to: string | null
  notes: string | null
  is_active: boolean
  created_at: string | null
  // Sprint 0144 follow-up: audit trail surfaced for the Settings page
  // "Updated at / by" column.
  updated_at: string | null
  created_by: string | null
  updated_by: string | null
}

export interface CardAlert {
  id: string
  plant_id: string
  machine_id: string
  production_log_id: string | null
  count_id: string | null
  shift_id: string | null
  alert_type: CardAlertType
  severity: AlertSeverity
  status: AlertStatus
  message: string
  standard_value: number | null
  actual_value: number | null
  deviation_pct: number | null
  context: Record<string, unknown> | null
  raised_at: string
  acknowledged_at: string | null
  acknowledged_by: string | null
  resolved_at: string | null
  resolved_by: string | null
  resolution_notes: string | null
}

export interface CalcResult {
  sliver_ktex: number | null
  can_content_kg: number | null
  theoretical_production_kg: number | null
  expected_production_kg: number | null
  actual_efficiency_pct: number | null
  cans_produced: number | null
  confidence: 'EXACT' | 'DATA_NOT_AVAILABLE'
}

export interface AlertSummary {
  by_status: Record<AlertStatus, number>
  by_severity: Record<AlertSeverity, number>
  open_critical: number
  total: number
}

// ─── API ────────────────────────────────────────────────────────────

export const cardingApi = {
  // Settings master
  listSettings: async (params: {
    plant_id: string
    machine_id?: string
    count_id?: string
    active_only?: boolean
  }) =>
    (await apiClient.get<CardStandard[]>('/carding/settings', { params })).data,

  createSetting: async (body: Partial<CardStandard> & {
    plant_id: string
    card_sliver_hank: number
    delivery_speed_m_min: number
    effective_from: string
  }) =>
    (await apiClient.post<CardStandard>('/carding/settings', body)).data,

  updateSetting: async (id: string, body: Partial<CardStandard>) =>
    (await apiClient.patch<CardStandard>(`/carding/settings/${id}`, body)).data,

  // Download Excel — respects the same filters as listSettings so the
  // export matches what's on screen. Triggers a browser save via blob URL.
  exportSettings: async (params: {
    plant_id: string
    machine_id?: string
    count_id?: string
    active_only?: boolean
  }) => {
    const res = await apiClient.get<Blob>(
      '/carding/settings/export.xlsx',
      { params, responseType: 'blob' },
    )
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'carding_settings.xlsx'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 100)
  },

  // Sprint 0146 — Quick Entry from LMW machine display.
  // Server creates the ProductionLog, runs all derivations, persists
  // fault root-cause entries, fires the alert engine, returns the full
  // calc bundle in one round-trip.
  quickEntry: async (body: {
    plant_id: string
    machine_id: string
    shift_id: string
    log_date: string                          // YYYY-MM-DD
    count_id?: string | null
    hanks_produced?: number | null            // LMW "Hank" field
    production_kg?: number | null             // LMW "Kgs"
    run_time_minutes?: number | null          // LMW "Run Time" hh:mm flattened
    idle_time_minutes?: number | null
    power_fail_minutes?: number | null
    machine_efficiency_pct?: number | null    // LMW "M/c Eff (%)"
    faults: Array<{
      fault_type: 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK'
                | 'CHUTE_FAULTS' | 'DOFFS'
      count: number
      root_cause?: string | null
      corrective_action?: string | null
    }>
    remarks?: string | null
  }) => (await apiClient.post<{
    log_id: string
    log_no: string
    calc: {
      sliver_ktex: number | null
      can_content_kg: number | null
      theoretical_production_kg: number | null
      expected_production_kg: number | null
      actual_efficiency_pct: number | null
      cans_produced: number | null
      production_kg: number | null
      hanks_produced: number | null
      hank_kg_consistency: {
        status: 'MATCH' | 'MISMATCH' | 'DATA_NOT_AVAILABLE'
        deviation_pct: number | null
        expected_hanks_from_kg: number | null
        implied_hank_ne_from_both: number | null
      }
      confidence: string
    }
    fault_entries: Array<{
      fault_type: string; count: number
      root_cause: string | null; corrective_action: string | null
    }>
    alerts_raised: number
    standard_applied: string | null
  }>('/carding/quick-entry', body)).data,

  // Calculator (Section 17/18 formulas)
  calc: async (body: {
    sliver_hank?: number
    can_length_m?: number
    delivery_speed_mpm?: number
    runtime_hours?: number
    actual_production_kg?: number
    standard_efficiency_pct?: number
  }) =>
    (await apiClient.post<CalcResult>('/carding/calc', body)).data,

  // Alerts
  listAlerts: async (params: {
    plant_id: string
    status?: AlertStatus
    severity?: AlertSeverity
    machine_id?: string
    production_log_id?: string
    alert_type?: CardAlertType
    limit?: number
  }) => (await apiClient.get<CardAlert[]>('/carding/alerts', { params })).data,

  alertSummary: async (plant_id: string) =>
    (await apiClient.get<AlertSummary>('/carding/alerts/summary', {
      params: { plant_id },
    })).data,

  acknowledgeAlert: async (id: string, body: { notes?: string } = {}) =>
    (await apiClient.post<CardAlert>(`/carding/alerts/${id}/ack`, body)).data,

  resolveAlert: async (id: string, body: { notes?: string } = {}) =>
    (await apiClient.post<CardAlert>(`/carding/alerts/${id}/resolve`, body)).data,

  rebuildAlerts: async (log_id: string) =>
    (await apiClient.post<{ log_id: string; alerts_created: number }>(
      `/carding/alerts/rebuild/${log_id}`,
    )).data,

  // ─── Sprint 0141 — Card waste studies ──────────────────────────────
  listStudies: async (params: {
    plant_id: string
    card_machine_id?: string
    from_date?: string
    to_date?: string
    status?: CardWasteStudyStatus
    limit?: number
  }) =>
    (await apiClient.get<CardWasteStudy[]>('/carding/waste-studies', { params })).data,

  studiesTrend: async (params: {
    plant_id: string
    card_machine_id: string
    months?: number
  }) =>
    (await apiClient.get<Array<{
      study_date: string
      waste_pct: number | null
      realization_pct: number | null
      ambient_rh_pct: number | null
    }>>('/carding/waste-studies/trend', { params })).data,

  getStudy: async (id: string) =>
    (await apiClient.get<CardWasteStudy>(`/carding/waste-studies/${id}`)).data,

  createStudy: async (body: {
    plant_id: string
    card_machine_id: string
    count_id?: string
    study_date: string
    shift_id?: string
    ambient_temp_c?: number
    ambient_rh_pct?: number
    sample_duration_min?: number
    notes?: string
  }) =>
    (await apiClient.post<CardWasteStudy>('/carding/waste-studies', body)).data,

  patchStudy: async (id: string, body: {
    count_id?: string
    study_date?: string
    shift_id?: string
    ambient_temp_c?: number
    ambient_rh_pct?: number
    sample_duration_min?: number
    sample_output_kg?: number
    // Sprint 0142 — mill-spec components
    lickerin_kg?: number
    flat_strips_kg?: number
    sfl_kg?: number
    sfd_kg?: number
    doffer_kg?: number
    under_feed_roll_kg?: number
    // Sprint 0141 legacy generics
    cylinder_kg?: number
    sliver_clearer_kg?: number
    framework_fly_kg?: number
    notes?: string
  }) =>
    (await apiClient.patch<CardWasteStudy>(`/carding/waste-studies/${id}`, body)).data,

  completeStudy: async (id: string) =>
    (await apiClient.post<CardWasteStudy>(`/carding/waste-studies/${id}/complete`)).data,

  signStudy: async (id: string) =>
    (await apiClient.post<CardWasteStudy>(`/carding/waste-studies/${id}/sign`)).data,

  // ─── Sprint 0142 — Section Studies (all N cards in parallel) ──────
  listSections: async (params: {
    plant_id: string
    from_date?: string
    to_date?: string
    status?: CardWasteStudyStatus
    limit?: number
  }) => (await apiClient.get<CardWasteSectionStudy[]>(
    '/carding/waste-studies/sections', { params },
  )).data,

  getSection: async (id: string) =>
    (await apiClient.get<{
      section: CardWasteSectionStudy
      children: CardWasteStudy[]
    }>(`/carding/waste-studies/sections/${id}`)).data,

  createSection: async (body: {
    plant_id: string
    study_date: string
    count_id?: string
    ambient_temp_c?: number
    ambient_rh_pct?: number
    sample_duration_min?: number
    notes?: string
    card_machine_ids?: string[]
  }) => (await apiClient.post<{
    section: CardWasteSectionStudy
    children: CardWasteStudy[]
  }>('/carding/waste-studies/sections', body)).data,

  completeSection: async (id: string) =>
    (await apiClient.post<{
      section: CardWasteSectionStudy
      children: CardWasteStudy[]
    }>(`/carding/waste-studies/sections/${id}/complete`)).data,

  signSection: async (id: string) =>
    (await apiClient.post<{
      section: CardWasteSectionStudy
      children: CardWasteStudy[]
    }>(`/carding/waste-studies/sections/${id}/sign`)).data,

  exportSectionXlsx: async (id: string) =>
    apiClient.get(`/carding/waste-studies/sections/${id}/export.xlsx`, {
      responseType: 'blob',
    }),

  // ─── Sprint 0143 — Can summary for Daily Shift Log ──────────────────
  canSummary: async (params: {
    plant_id: string
    log_date: string
    shift_id?: string
  }) => (await apiClient.get<CardingCanSummary>('/carding/can-summary', { params })).data,
}

// Sprint 0143 — Can summary types
export interface CardingCanPerCard {
  machine_id: string
  machine_code: string | null
  machine_name: string | null
  log_id: string | null
  std_can_kg: number
  produced_count: number
  produced_kg: number
  produced_count_derived?: number  // fallback when Can table empty
  produced_kg_derived?: number
  target_count: number
  target_kg: number
  in_chute_count: number
  qc_pending_count: number
  qc_held_count: number
  qc_rejected_count: number
  transferred_to_drawing_count: number
  consumed_count: number
  partial_can_kg: number
}

export interface CardingCanSection {
  produced_count: number
  produced_kg: number
  target_count: number
  target_kg: number
  in_chute_count: number
  qc_pending_count: number
  qc_held_count: number
  qc_rejected_count: number
  transferred_to_drawing_count: number
  consumed_count: number
  partial_can_kg: number
  empties_available_count: number
}

export interface CardingCanSummary {
  log_date: string
  shift_id: string | null
  per_card: CardingCanPerCard[]
  section: CardingCanSection
}

// ─── Sprint 0141 — Card Waste Study types ───────────────────────────
export type CardWasteStudyStatus = 'DRAFT' | 'COMPLETED' | 'SIGNED'

export interface CardWasteStudy {
  id: string
  plant_id: string
  card_machine_id: string
  count_id: string | null
  study_date: string
  shift_id: string | null
  measured_by: string | null
  ambient_temp_c: number | null
  ambient_rh_pct: number | null
  sample_duration_min: number
  sample_output_kg: number | null
  sample_waste_kg: number | null
  sample_input_kg: number | null
  waste_pct: number | null
  realization_pct: number | null
  // Sprint 0142 — Mill-spec LMW C5/1 collection points
  lickerin_kg: number | null
  flat_strips_kg: number | null
  sfl_kg: number | null
  sfd_kg: number | null
  doffer_kg: number | null
  under_feed_roll_kg: number | null
  // Sprint 0141 legacy generics (back-compat only, not surfaced in matrix)
  cylinder_kg: number | null
  sliver_clearer_kg: number | null
  framework_fly_kg: number | null
  notes: string | null
  qm_signed_at: string | null
  qm_signed_by: string | null
  status: CardWasteStudyStatus
  applied_to_standard_id: string | null
  applied_at: string | null
  created_at: string | null
  // Sprint 0142 — links to parent section study (NULL = ad-hoc single)
  section_study_id: string | null
}

// Sprint 0142 — Section study (parent of N child studies)
export interface CardWasteSectionStudy {
  id: string
  plant_id: string
  study_date: string
  count_id: string | null
  ambient_temp_c: number | null
  ambient_rh_pct: number | null
  sample_duration_min: number
  notes: string | null
  status: CardWasteStudyStatus
  qm_signed_at: string | null
  qm_signed_by: string | null
  created_by: string | null
  created_at: string | null
}
